/**
 * @file Conteneur de page unifie pour le dashboard.
 * @module common.components.page-container.PageContainer
 *
 * Composant unique de cadrage horizontal du contenu des pages.
 * Gere le maxWidth et le centrage (mx: auto), sans aucun padding
 * horizontal — celui-ci est la responsabilite exclusive de main.js.
 *
 * Respecte le reglage themeStretch : quand il est actif, les pages
 * utilisant un breakpoint MUI ('sm'|'md'|'lg'|'xl') passent en pleine
 * largeur. Les valeurs numeriques explicites (ex. 1100) ne sont pas
 * affectees par themeStretch — elles restent fixes.
 */

import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import {useSettingsContext} from 'packages/common/components/settings';

// Correspondance breakpoint MUI -> pixels
const BREAKPOINT_MAP = {
  xs: 444,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

/**
 * Conteneur de page avec largeur maximale et centrage automatique.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Contenu de la page
 * @param {string|number|false} [props.maxWidth='xl'] - Largeur maximale
 *   (breakpoint MUI, valeur en px, ou false pour pleine largeur)
 * @param {Object} [props.sx] - Styles MUI supplementaires
 * @return {JSX.Element}
 */
export default function PageContainer({children, maxWidth = 'xl', sx, ...other}) {
  const {themeStretch} = useSettingsContext();

  // Breakpoint string : soumis a themeStretch
  // Valeur numerique : fixe (ignore themeStretch)
  // false : pleine largeur
  let resolvedMaxWidth;
  if (maxWidth === false) {
    resolvedMaxWidth = false;
  } else if (typeof maxWidth === 'string') {
    resolvedMaxWidth = themeStretch ? false : (BREAKPOINT_MAP[maxWidth] || false);
  } else {
    resolvedMaxWidth = maxWidth;
  }

  return (
    <Box
      sx={{
        width: '100%',
        mx: 'auto',
        ...(resolvedMaxWidth && {maxWidth: resolvedMaxWidth}),
        ...sx,
      }}
      {...other}
    >
      {children}
    </Box>
  );
}

PageContainer.propTypes = {
  children: PropTypes.node,
  maxWidth: PropTypes.oneOfType([
    PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
    PropTypes.number,
  ]),
  sx: PropTypes.object,
};
